import type { ReactNode } from 'react';

import { cn } from '../lib/cn.js';

export type EmptyStateProps = {
  eyebrow?: string;
  title: string;
  description: ReactNode;
  cta?: ReactNode;
  footnote?: ReactNode;
  size?: 'md' | 'lg';
};

/*
 * Centered empty state used wherever a screen has no data to render. The
 * eyebrow is the small mono uppercase line above the title; `size="lg"`
 * bumps the title to text-2xl for hero moments (e.g. first-game setup).
 */
export function EmptyState({
  eyebrow,
  title,
  description,
  cta,
  footnote,
  size = 'md',
}: EmptyStateProps): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center px-4 py-8 sm:px-6 sm:py-16">
      <div className="max-w-md rounded-sm border border-border bg-surface px-6 py-8 text-center sm:px-8 sm:py-10">
        {eyebrow !== undefined && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-text-muted">
            {eyebrow}
          </p>
        )}
        <h2
          className={cn(
            'mt-2 font-display font-bold text-text',
            size === 'lg' ? 'text-2xl' : 'text-xl',
          )}
        >
          {title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-text-muted">
          {description}
        </p>
        {cta !== undefined && (
          <div className={cn('flex justify-center', size === 'lg' ? 'mt-6' : 'mt-5')}>
            {cta}
          </div>
        )}
        {footnote !== undefined && (
          <p className="mt-3 text-xs text-text-muted">{footnote}</p>
        )}
      </div>
    </div>
  );
}
